import { Spacer } from "@hackthenorth/north";
import { Button, Checkbox, InlineError, Table, Text } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { GooseGameTransactionType } from "src/api/types.generated";
import { errorToast, Icon, successToast } from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { ArrowRight } from "src/static/icons";

import { BalanceChip } from "../components/balanceChip";
import Counter from "../components/counter";
import CheckoutModal from "../components/modals/organizer/hacker_scan/checkout";
import { useGetGooseGameStoreItemsQuery } from "../graphql/getGooseGameStoreItems.generated";
import { GGPlayer, Status, TPrizeCheckout } from "../types";

import { useGetPurchaseTransactionsQuery } from "./graphql/getPurchaseTransactions.generated";
import { usePurchaseGooseGameStoreItemsMutation } from "./graphql/purchaseGooseGameStoreItem.generated";

interface PrizeCheckoutProp {
  GooseGamePlayer: GGPlayer;
  setRefreshHistory: (refreshHistory: boolean) => void;
}
const PrizeCheckout: React.FC<PrizeCheckoutProp> = ({
  GooseGamePlayer,
  setRefreshHistory,
}) => {
  const { data: gooseGameItemsData } = useGetGooseGameStoreItemsQuery({
    variables: { event_id: EVENT_ID },
  });

  const { data: transactionData } = useGetPurchaseTransactionsQuery({
    variables: { event_attendee_id: GooseGamePlayer.event_attendee_id },
  });

  const [purchaseGooseGameStoreItems] =
    usePurchaseGooseGameStoreItemsMutation();

  const [prizes, setPrizes] = useState<TPrizeCheckout[]>([]);
  const [total, setTotal] = useState(0);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [duplicatePurchase, setDuplicatePurchase] = useState(false);

  const noSelection = prizes.every((prize) => prize.quantity === 0);
  const insufficientFunds = GooseGamePlayer.current_points < total;
  const isButtonDisabled = noSelection || insufficientFunds;

  const tableColumns = [
    {
      id: "selected",
      name: "select",
      hideLabel: true,
    },
    {
      id: "prize",
      name: "Prize",
    },
    {
      id: "status",
      name: "Status",
    },
    {
      id: "price",
      name: "Price",
    },
    {
      id: "quantity",
      name: "QTY",
    },
  ];

  useEffect(() => {
    if (transactionData) {
      const purchasedItemIds = new Set(
        transactionData.gooseGameTransactions
          .filter((t) => t.type === GooseGameTransactionType.Purchase)
          .map((t) => t.item_id)
      );

      const hasDuplicate = prizes.some(
        (prize) => purchasedItemIds.has(prize.id) && prize.quantity > 0
      );

      setDuplicatePurchase(hasDuplicate);
    }
  }, [transactionData, prizes]);

  useEffect(() => {
    if (gooseGameItemsData) {
      const gooseGamePrizes: TPrizeCheckout[] =
        gooseGameItemsData.gooseGameStoreItems.map((prize) => ({
          id: prize.id,
          name: prize.name,
          description: prize.description,
          pointCost: prize.point_cost,
          quantityAvailable: prize.quantity_available,
          redemptionLimit: prize.user_redemption_limit,
          isSoldOut: prize.is_sold_out,
          quantity: 0,
          selected: false,
        }));

      setPrizes(gooseGamePrizes);
    }
  }, [gooseGameItemsData]);

  useEffect(() => {
    const newTotal = prizes.reduce(
      (acc, prize) => acc + prize.quantity * prize.pointCost,
      0
    );
    setTotal(newTotal);
  }, [prizes]);

  const handleQuantityChange = (index: number, newQty: number) => {
    const checked = newQty > 0;
    setPrizes((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, quantity: newQty, selected: checked } : p
      )
    );
  };

  const handleSelectedChange = (index: number, prevChecked: boolean) => {
    const newChecked = !prevChecked;
    setPrizes((prev) =>
      prev.map((p, i) =>
        i === index
          ? { ...p, selected: newChecked, quantity: !newChecked ? 0 : 1 }
          : p
      )
    );
  };

  const handleCheckout = async () => {
    if (GooseGamePlayer.current_points < total) return;
    const purchaseItems = prizes
      .filter((prize) => prize.quantity > 0)
      .map((prize) => ({
        item_id: prize.id,
        quantity: prize.quantity,
      }));

    try {
      await purchaseGooseGameStoreItems({
        variables: {
          event_attendee_id: GooseGamePlayer.event_attendee_id,
          items: purchaseItems,
          event_id: EVENT_ID,
        },
      });

      const itemList = prizes
        .filter((prize) => prize.quantity > 0)
        .map((prize) => `${prize.name} (x${prize.quantity})`)
        .join(" and ");

      successToast(
        `You successfully redeemed ${itemList} for ${GooseGamePlayer.event_attendee?.user.name}.`
      );

      setPrizes([]);
    } catch (error) {
      errorToast("Unable to complete checkout.");
    } finally {
      setRefreshHistory(true);
    }

    setIsCheckoutModalOpen(false);
  };
  const tableData = prizes.map((prize, index) => {
    const status = !prize.isSoldOut ? Status.AVAILABLE : Status.UNAVAILABLE;

    return {
      selected: (
        <Checkbox
          checked={prize.selected}
          onClick={() => handleSelectedChange(index, prize.selected)}
          disabled={prize.isSoldOut}
        />
      ),
      prize: (
        <PrizeText
          type="body1"
          availablity={prize.isSoldOut ? "soldout" : "available"}
        >
          {prize.name}
        </PrizeText>
      ),
      status: (
        <StyledLabel availablity={prize.isSoldOut ? "soldout" : "available"}>
          <StyledText
            type="body2"
            availablity={prize.isSoldOut ? "soldout" : "available"}
          >
            {status}
          </StyledText>
        </StyledLabel>
      ),
      price: (
        <BalanceChip points={prize.pointCost} isComplete={prize.isSoldOut} />
      ),
      quantity: (
        <Counter
          value={prize.quantity}
          min={0}
          max={prize.redemptionLimit}
          disabled={prize.isSoldOut}
          onChange={(newQty) => handleQuantityChange(index, newQty)}
        />
      ),
    };
  });

  return (
    <Container>
      <Table
        displayStyle={"divide-y"}
        columns={tableColumns}
        data={tableData}
        horizontalPadding={16}
        verticalPadding={10}
      />
      <Spacer height={16} />
      <CheckoutContainer>
        <TotalContainer>
          <Text type="subtitle">Total</Text>
          <BalanceChip points={total} />
        </TotalContainer>
        <CheckoutButton
          size="md"
          onClick={() => setIsCheckoutModalOpen(true)}
          disabled={isButtonDisabled}
        >
          Checkout
          <ArrowRight width={16} />
        </CheckoutButton>
      </CheckoutContainer>
      <Spacer height={4} />
      {insufficientFunds && <InlineError>Insufficient funds</InlineError>}
      {duplicatePurchase && (
        <InlineWarning>
          <Icon name="warning-circle" width="13px" />
          <WarningText>
            Warning: This hacker has already redeemed one of these items before.
          </WarningText>
        </InlineWarning>
      )}
      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onCancel={() => setIsCheckoutModalOpen(false)}
        onConfirm={handleCheckout}
        items={prizes}
        currentBalance={GooseGamePlayer.current_points}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 36px;
  max-width: 680px;
  width: 100%;
`;

const CheckoutContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
`;

const TotalContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const PrizeText = styled(Text)<{ availablity: "available" | "soldout" }>`
  color: ${({ theme, availablity }) =>
    availablity === "soldout"
      ? theme.globalConstants.color.textGrey
      : theme.globalConstants.color.textBody};
  font-weight: 900;
`;

const StyledLabel = styled.div<{ availablity: "available" | "soldout" }>`
  padding: 4px 9.19px;
  align-items: center;
  border-radius: 9999px;
  width: max-content;
  background-color: ${({ theme, availablity }) =>
    availablity === "available"
      ? theme.globalConstants.color.backgroundSuccessLight
      : theme.globalConstants.color.backgroundDangerLight} !important;
  font-weight: 700;
`;

const StyledText = styled(Text)<{ availablity: "available" | "soldout" }>`
  color: ${({ theme, availablity }) =>
    availablity === "available"
      ? theme.globalConstants.color.textSuccess
      : theme.globalConstants.color.textDanger} !important;
`;

const CheckoutButton = styled(Button)<{ disabled: boolean }>`
  background-color: ${({ theme, disabled }) =>
    disabled
      ? theme.globalConstants.color.textTertiary
      : theme.globalConstants.color.backgroundPrimary} !important;
  color: ${({ theme }) => theme.globalConstants.color.textLight} !important;
  font-weight: 700;
`;

const InlineWarning = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 0 1px;
  height: max-content;
`;

const WarningText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textWarning} !important;
  font-size: 12px !important;
  line-height: 160% !important;
`;

export default PrizeCheckout;
